import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import TextEditor from 'components/field/TextEditor';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const LanguageResourceManagementModal = NiceModal.create(({ row }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					langKey: yup.string().nullable().required(),
					engValue: yup.string().nullable(),
					trValue: yup.string().nullable(),
					arabicValue: yup.string().nullable()
				})
				.required(),
		[]
	);

	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const [editorView, setEditorView] = useState(false);

	const handleCloseModal = () => {
		modal.remove();
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: row
			? {
					langKey: row.key,
					...row
				}
			: {
					langKey: null,
					engValue: null,
					trValue: null,
					arabicValue: null
				}
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		const URL = row?.ID
			? ADMIN.LANGUAGE_RESOURCE.UPDATE_LANGUAGE_RESOURCE
			: ADMIN.LANGUAGE_RESOURCE.ADD_LANGUAGE_RESOURCE;

		await axiosPrivate
			.post(URL, {
				ID: row?.ID,
				...data,
				langKey: data.langKey?.trim()
			})
			.then((response) => {
				const { status } = response;

				if (status !== 200) {
					showError();
					return;
				}

				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.LANGUAGE_RESOURCE_MANAGEMENT));
				handleCloseModal();
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	return (
		<Modal fullwidth maxWidth="xx-large" className="modal-languageResourceManagementModal">
			<ModalTitle>
				<Typography variant="p1">
					{row ? (row?.ID ? 'updateLanguageResource' : 'duplicateLanguageResource') : 'addLanguageResource'}
				</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="key" required>
						<StringInput name="langKey" />
					</FormItem>
					<FormItem label="english">
						{editorView ? (
							<TextEditor
								value={watch('engValue')}
								setValue={(val) => {
									setValue('engValue', val, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
							/>
						) : (
							<TextArea name="engValue" maxlength="100000" />
						)}
					</FormItem>
					<FormItem label="turkish">
						{editorView ? (
							<TextEditor
								value={watch('trValue')}
								setValue={(val) => {
									setValue('trValue', val, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
							/>
						) : (
							<TextArea name="trValue" maxlength="100000" />
						)}
					</FormItem>
					<FormItem label="arabic">
						{editorView ? (
							<TextEditor
								value={watch('arabicValue')}
								setValue={(val) => {
									setValue('arabicValue', val, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
							/>
						) : (
							<TextArea name="arabicValue" maxlength="100000" />
						)}
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					variant="black"
					label="htmlEditorView"
					onClick={() => {
						if (editorView) {
							setEditorView(false);
						} else {
							NiceModal.show(ConfirmationModal, {
								title: 'htmlEditorViewConfirmationTitle',
								subTitle: 'htmlEditorViewConfirmationMessage',
								confirmLabel: 'iDefinitelyWantToUseHtmlEditorAndIKnowWhatImDoing',
								yesAction: () => {
									setEditorView(true);
								},
								maxWidth: 'medium'
							});
						}
					}}
				/>
				<Button icon={faTimes} variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label={row?.ID ? 'update' : 'add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default LanguageResourceManagementModal;
